import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { CategoryDefDto } from '@tww/shared';
import { api } from '../../api/client';
import { useCategoryDefs } from '../../api/hooks';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ErrorState, Note, Panel, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';
import { KIND_LABEL, moveItem } from './adminUtil';
import { SortCell } from './SortCell';

export function CategoriesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const defs = useCategoryDefs();
  const [drag, setDrag] = useState<number | null>(null);
  const [removing, setRemoving] = useState<CategoryDefDto | null>(null);
  const inv = () => qc.invalidateQueries({ queryKey: ['contact-categories'] });
  const order = useMutation({
    mutationFn: (keys: string[]) => api.put('/contact-categories/order', { keys }),
    onSuccess: inv,
    onError: (e: Error) => toast(`Reihenfolge nicht gespeichert: ${e.message}`, 'error'),
  });
  const toggle = useMutation({
    mutationFn: (d: CategoryDefDto) => api.patch(`/contact-categories/${d.key}`, { is_active: !d.is_active }),
    onSuccess: (_r, d) => {
      inv();
      toast(d.is_active ? `„${d.name}“ deaktiviert` : `„${d.name}“ aktiviert`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  if (defs.isLoading) return <Skeleton />;
  if (defs.isError) return <ErrorState error={defs.error} retry={() => defs.refetch()} />;
  const items = defs.data!;
  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= items.length) return;
    order.mutate(moveItem(items.map((d) => d.key), from, to));
  };
  return (
    <>
      <Note kind="info">Neue Kategorien erscheinen sofort in Filtern, Auswahllisten und Kontaktakten. Systemkategorien lassen sich nicht löschen, aber um Felder ergänzen.</Note>
      <Panel
        title="Kontaktkategorien"
        action={
          <Link className="btn sm primary" to="/verwaltung/kontaktkategorien/neu">
            <Icon name="plus" /> Kategorie
          </Link>
        }
      >
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th className="col-sort">
                  <span className="sr-only">Reihenfolge</span>
                </th>
                <th>Name</th>
                <th>Für</th>
                <th>Felder</th>
                <th>Aktiv</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((d, i) => (
                <tr key={d.key} className={d.is_active ? '' : 'row-off'} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag != null) move(drag, i); setDrag(null); }}>
                  <SortCell index={i} count={items.length} label={d.name} onMove={move} busy={order.isPending} />
                  <td>
                    {d.name} <span className="mut small">({d.name_plural})</span>
                    {d.is_system && <span className="sm">Systemkategorie</span>}
                  </td>
                  <td className="small">{d.kinds_allowed.map((k) => KIND_LABEL[k] ?? k).join(', ')}</td>
                  <td className="small">{d.fields.length ? d.fields.map((f) => f.label).join(', ') : <span className="mut">keine</span>}</td>
                  <td>
                    <input type="checkbox" checked={d.is_active} disabled={d.is_system || toggle.isPending} onChange={() => toggle.mutate(d)} aria-label={`${d.name} aktiv`} title={d.is_system ? 'Systemkategorien sind immer aktiv' : undefined} />
                  </td>
                  <td className="num nowrap">
                    <Link className="btn sm" to={`/verwaltung/kontaktkategorien/${d.key}`}>
                      Bearbeiten
                    </Link>{' '}
                    {!d.is_system && (
                      <button className="btn sm danger" type="button" onClick={() => setRemoving(d)}>
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      {removing && (
        <RemoveDialog
          def={removing}
          onClose={() => setRemoving(null)}
          onDone={() => {
            inv();
            setRemoving(null);
          }}
        />
      )}
    </>
  );
}

/** Löschen mit Rückfrage; wird die Kategorie verwendet, wird stattdessen Deaktivieren angeboten */
function RemoveDialog({ def, onClose, onDone }: { def: CategoryDefDto; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const usage = useQuery({
    queryKey: ['category-usage', def.key],
    queryFn: () => api.get<{ total: number }>(`/contacts?pageSize=1&filter[category]=${encodeURIComponent(def.key)}&filter[archived]=1`).then((r) => r.total),
    retry: false,
    staleTime: 0,
  });
  const n = usage.data ?? null;
  const used = n != null && n > 0;
  const run = useMutation({
    mutationFn: () => (used ? api.patch(`/contact-categories/${def.key}`, { is_active: false }).then(() => ({ deactivated: true })) : api.delete<{ deactivated?: boolean }>(`/contact-categories/${def.key}`)),
    onSuccess: (r) => {
      toast(r.deactivated ? `„${def.name}“ deaktiviert` : `„${def.name}“ gelöscht`);
      onDone();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const checking = usage.isLoading;
  const title = used ? 'Kategorie wird verwendet' : `Kategorie „${def.name}“ löschen?`;
  let text: string;
  if (checking) text = 'Wird geprüft …';
  else if (used && !def.is_active) text = `${n === 1 ? '1 Kontakt ist' : `${n} Kontakte sind`} „${def.name}“ zugeordnet. Die Kategorie ist bereits deaktiviert und lässt sich nicht löschen, solange Kontakte zugeordnet sind.`;
  else if (used) text = `${n === 1 ? '1 Kontakt ist' : `${n} Kontakte sind`} „${def.name}“ zugeordnet. Die Kategorie lässt sich daher nicht löschen. Du kannst sie deaktivieren: Sie verschwindet aus den Auswahllisten, die Zuordnungen bleiben erhalten.`;
  else if (n === 0) text = 'Die Kategorie ist keinem Kontakt zugeordnet und wird endgültig gelöscht.';
  else text = 'Die Kategorie wird gelöscht. Ist sie noch Kontakten zugeordnet, wird sie stattdessen deaktiviert.';
  const canAct = !(used && !def.is_active);
  if (canAct)
    return <ConfirmDialog title={title} text={text} confirmLabel={used ? 'Deaktivieren' : 'Löschen'} danger={!used} busy={run.isPending || checking} onConfirm={() => run.mutate()} onClose={onClose} />;
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <button className="btn" type="button" onClick={onClose}>
          Schließen
        </button>
      }
    >
      {text}
    </Modal>
  );
}
